<template>
	<v-card :data-test="dataTest ? `${dataTest}-card` : undefined" class="pa-0">
		<BaseCardTitle
			v-if="
				!loading &&
				(title ||
					subtitle ||
					prependIcon ||
					$attrs['append-icon'] ||
					$slots.title ||
					$slots.subtitle ||
					$slots.append ||
					$slots.prepend)
			"
			:prepend-icon="prependIcon"
			:subtitle="subtitle"
			:title="title"
			:close-btn="closeBtn"
			@close="$emit('close')"
		>
			<template v-if="$slots.title" #title>
				<div :data-test="`${cardDataTest}__title`">
					<slot name="title"></slot>
				</div>
			</template>
			<template v-if="$slots.subtitle" #subtitle>
				<div :data-test="`${cardDataTest}__subtitle`">
					<slot name="subtitle"></slot>
				</div>
			</template>
			<template v-if="$slots.append" #append>
				<slot name="append"></slot>
			</template>
			<template v-if="$slots.prepend" #prepend>
				<slot name="append"></slot>
			</template>
		</BaseCardTitle>

		<template v-if="!loading">
			<v-divider v-if="['both', 'top'].includes(divider)" />

			<slot
				v-if="!loading"
				v-bind="{ dataTest: `${cardDataTest}__loading` }"
			></slot>

			<v-card-text
				v-if="$slots.text || text"
				:class="textClass"
				:data-test="`${cardDataTest}__text`"
			>
				<slot
					v-bind="{ dataTest: `${cardDataTest}__text`, text, textClass }"
					name="text"
				>
					{{ text }}</slot
				>
			</v-card-text>

			<v-divider v-if="['both', 'bottom'].includes(divider)" />

			<v-card-actions
				v-if="$slots.actions"
				:class="actionClass"
				:data-test="`${cardDataTest}__actions`"
			>
				<v-spacer v-if="actionSpacer" />
				<slot
					v-bind="{ dataTest: `${cardDataTest}__actions`, actionClass }"
					name="actions"
				></slot>
			</v-card-actions>
		</template>
	</v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseCardTitle from './BaseCardTitle.vue'
import type { CardPropsFull } from './CardProps'

const props = withDefaults(
	defineProps<CardPropsFull & { divider?: 'top' | 'bottom' | 'both' }>(),
	{
		dataTest: undefined,
		loading: false,
		divider: undefined,
	}
)

defineEmits(['close'])

const cardDataTest = computed(() => `${props.dataTest}-card`)
</script>

<style lang="scss" scoped>
// AAR-976: On small screens nested (inner) cards sat too far from the outer
// card edge because the card body kept its full 16px padding. Tighten the body
// padding on xs only for better space usage; larger breakpoints keep the
// roomier default.
@media (max-width: 599.98px) {
	:deep(.v-card-text) {
		padding: 8px;
	}
}
</style>
